import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  arrayRemove,
  arrayUnion,
  doc,
  onSnapshot,
  updateDoc,
} from "firebase/firestore";
import { LazyLoadImage } from "react-lazy-load-image-component";
import { buildStyles, CircularProgressbar } from "react-circular-progressbar";
import { AiFillHeart } from "react-icons/ai";
import { resizeImage } from "../../shared/utils";
import { BsShareFill, BsThreeDots, BsFillPlayFill } from "react-icons/bs";
import { db } from "../../shared/firebase";
import { useAppSelector } from "../../store/hooks";
import { useCurrentViewportView } from "../../hooks/useCurrentViewportView";
import YouTube from "react-youtube";
import Sidebar from "../Common/Sidebar";
import SideFilmsCol from "../Common/SideFilmsCol";
import SearchBox from "../Common/SearchBox";
import SidebarMini from "../Common/SidebarMini";
import Skeleton from "../Common/Skeleton";
import PageTitle from "../Common/PageTitle";
import FilmTabInfo from "./FilmTabInfo";
import MobileNav from "../Common/MobileNav";
import Footer from "../Footer/Footer";
import toast from "react-hot-toast";

const FilmDetail = ({ similar, videos, detail, ...others }) => {
  const currentUser = useAppSelector((state) => state.auth.user);
  const [isFromWatchlist, setIsFromWatchlist] = useState(false);
  const { isMobile } = useCurrentViewportView();
  const [isSidebarActive, setIsSidebarActive] = useState(false);

  useEffect(() => {
    if (!currentUser) {
      return;
    }

    const unsubDoc = onSnapshot(doc(db, "users", currentUser.uid), (doc) => {
      setIsFromWatchlist(
        doc.data()?.watchlist.some((item) => item.id === detail?.id)
      );
    });

    return () => unsubDoc();

    // eslint-disable-next-line
  }, [currentUser, detail?.id]);

  const watchlistHandler = async () => {
    if (!currentUser) {
      toast("You need to sign in to create your watchlist !", {
        icon: "🔑",
      });
      return;
    }

    await updateDoc(doc(db, "users", currentUser.uid), {
      watchlist: !isFromWatchlist
        ? arrayUnion({
            poster_path: detail?.poster_path,
            id: detail?.id,
            vote_average: detail?.vote_average,
            media_type: detail?.media_type,
            genre_ids: detail?.genres?.map((genre) => genre.id),
            ...(detail?.media_type === "movie" && { title: detail?.title }),
            ...(detail?.media_type === "tv" && { name: detail?.name }),
          })
        : arrayRemove({
            poster_path: detail?.poster_path,
            id: detail?.id,
            vote_average: detail?.vote_average,
            media_type: detail?.media_type,
            genre_ids: detail?.genres?.map((genre) => genre.id),
            ...(detail?.media_type === "movie" && { title: detail?.title }),
            ...(detail?.media_type === "tv" && { name: detail?.name }),
          }),
    });

    toast(
      `${
        !isFromWatchlist
          ? "This film is now added to your watchlist"
          : "This film is removed from your watchlist"
      }`,
      {
        icon: "🏷️",
      }
    );
  };

  return (
    <>
      {detail && (
        <PageTitle value={`${detail.title || detail.name} | SHWETFLIX`} />
      )}

      <MobileNav setIsSidebarActive={setIsSidebarActive} />

      <div className="flex flex-col md:flex-row ">
        {!isMobile && <SidebarMini />}
        {isMobile && (
          <Sidebar
            setIsSidebarActive={setIsSidebarActive}
            isSidebarActive={isSidebarActive}
          />
        )}
        <div className="flex-grow min-h-screen">
          {!detail && (
            <Skeleton className="h-[300px] md:h-[425px] rounded-bl-2xl "></Skeleton>
          )}
          {detail && (
            <div
              style={{
                backgroundImage: `url(${resizeImage(detail.backdrop_path)})`,
                backgroundPosition: "top center",
              }}
              className="bg-cover bg-center bg-no-repeat h-[300px] md:h-[425px] relative rounded-bl-2xl"
            >
              <div className="bg-gradient-to-bl from-transparent to-black/70 h-full rounded-bl-2xl">
                <div className="flex flex-col md:flex-row bottom-[-85%] md:bottom-0 lg:bottom-[-20%]  items-start absolute left-1/2 -translate-x-1/2  w-full max-w-[1000px] ">
                  <div className="flex gap-5 items-center justify-between w-full md:justify-center md:w-fit">
                    <div className="shrink-0 max-w-[185px] w-full ml-3 md:ml-0">
                      <LazyLoadImage
                        src={resizeImage(detail.poster_path, "w185")}
                        effect="opacity"
                        className="w-full h-full object-cover rounded-md"
                        alt="Poster"
                      />
                    </div>
                    {isMobile && (
                      <Link
                        to="watch"
                        className="flex gap-3 justify-center items-center px-5 py-3 mt-24 rounded-full mr-3
                                         bg-gradient-to-br from-primary to-orange-600 text-white group hover:scale-105 transition duration-500"
                      >
                        <p className="text-lg font-medium">WATCH</p>
                        <div className="w-10 h-10 rounded-full bg-white flex items-center justify-center">
                          <BsFillPlayFill
                            size={30}
                            className="text-orange-700 group-hover:animate-spinplay transition duration-700"
                          />
                        </div>
                      </Link>
                    )}
                  </div>
                  <div className="flex-grow md:ml-14 ml-6 mt-6 md:mt-0">
                    <div className="md:h-28 flex items-end">
                      <h1 className=" text-white md:text-[45px] text-[35px] font-bold leading-tight">
                        {detail.title || detail.name}
                      </h1>
                    </div>
                    <ul className="flex gap-3 flex-wrap md:mt-7 mt-3">
                      {detail.genres.slice(0, 3).map((genre) => (
                        <li key={genre.id}>
                          <Link
                            to={`/explore?genre=${genre.id}`}
                            className="md:px-5 px-3 md:py-2 py-1  rounded-full uppercase text-sm font-medium transition duration-300 
                                    shadow-md shadow-gray-darken text-white bg-dark-darken hover:bg-white hover:text-dark-darken"
                          >
                            {genre.name}
                          </Link>
                        </li>
                      ))}
                    </ul>
                  </div>
                  {!isMobile && (
                    <Link
                      to="watch"
                      className="flex gap-3 justify-center items-center px-5 py-3 mt-24 rounded-full 
                                        bg-gradient-to-br from-primary to-orange-600 text-white group hover:scale-105 transition duration-500"
                    >
                      <p className="text-lg font-medium">WATCH</p>
                      <div className="w-10 h-10 rounded-full bg-white flex items-center justify-center">
                        <BsFillPlayFill
                          size={30}
                          className="text-orange-700 group-hover:animate-spinplay transition duration-700"
                        />
                      </div>
                    </Link>
                  )}
                </div>

                <div className="flex flex-col sm:flex-row gap-3 absolute top-[5%] right-[3%]">
                  <button
                    className={`flex justify-center items-center h-10 w-10 rounded-full transition duration-300 
                                    shadow-md shadow-gray-darken text-white bg-dark-darken hover:bg-white group ${
                                      isFromWatchlist && "bg-white"
                                    }`}
                    onClick={watchlistHandler}
                  >
                    <AiFillHeart
                      size={18}
                      className={`text-white group-hover:text-primary transition duration-300 ${
                        isFromWatchlist && "text-primary"
                      }`}
                    />
                  </button>
                  <button
                    className="flex justify-center items-center  h-10 w-10 rounded-full transition duration-300 
                                    shadow-md shadow-gray-darken text-white bg-dark-darken hover:bg-white hover:text-dark-darken group"
                  >
                    <BsShareFill
                      size={16}
                      className="text-white group-hover:text-blue-700 transition duration-300"
                    />
                  </button>
                  <button
                    className="flex justify-center items-center h-10 w-10 rounded-full transition duration-300 
                                    shadow-md shadow-gray-darken text-white bg-dark-darken hover:bg-white hover:text-dark-darken group"
                  >
                    <BsThreeDots
                      size={18}
                      className="text-white group-hover:text-dark-darken transition duration-300"
                    />
                  </button>
                </div>
              </div>
            </div>
          )}
          <div className="flex z-20 relative flex-col md:flex-row mt-36 md:mt-0">
            <div className="shrink-0 md:max-w-[150px] w-full flex items-center md:flex-col justify-center flex-row gap-20 mt-20 md:border-r border-dark-lighten pt-16 md:justify-start lg:justify-center">
              <div className="flex flex-col gap-3 md:gap-6 items-center">
                <p className="text-white font-medium text-lg">RATING</p>
                <div className="w-16">
                  {detail && (
                    <CircularProgressbar
                      value={detail.vote_average}
                      maxValue={10}
                      text={`${detail.vote_average.toFixed(1)}`}
                      styles={buildStyles({
                        textSize: "30px",
                        pathColor: "#fff",
                        textColor: "#fff",
                        trailColor: "transparent",
                      })}
                    />
                  )}
                  {!detail && <Skeleton className="w-16 h-16 rounded-full" />}
                </div>
              </div>
              <div className="flex flex-col gap-6 md:gap-3 mb-4 items-center ">
                {(detail.runtime || detail.episode_run_time?.length > 0) && (
                  <>
                    <p className="text-white font-medium text-lg">
                      {detail.media_type === "movie" ? "RUNTIME" : "EP LENGTH"}
                    </p>
                    <div className="flex gap-2 items-center">
                      {detail.media_type === "movie" && (
                        <p className="text-2xl">{detail.runtime}</p>
                      )}
                      {detail.media_type === "tv" && (
                        <p className="text-2xl text-white">
                          {detail.episode_run_time[0]}
                        </p>
                      )}
                      <span>min</span>
                    </div>
                  </>
                )}
                {!detail && (
                  <>
                    <p className="text-white font-medium text-lg">RUNTIME</p>
                    <Skeleton className="w-16 h-6 mb-3 md:mb-0" />
                  </>
                )}
              </div>
            </div>
            <div className="flex flex-col lg:flex-row min-h-[500px] md:border-r border-dark-lighten lg:border-0">
              <div className="flex-grow min-h-[500px] lg:border-r border-dark-lighten px-5 py-7 md:px-12">
                <FilmTabInfo detail={detail} {...others} />
              </div>
              <div className="shrink-0 md:max-w-[300px] w-full px-6 lg:pt-6 md:px-12 lg:px-6">
                <p className="text-white font-medium text-lg mb-5">MEDIA</p>
                <ul className="flex flex-col items-center justify-center md:justify-start md:flex-wrap md:gap-[30px] gap-6">
                  {videos &&
                    videos.slice(0, 2).map((video) => (
                      <li
                        key={video.id}
                        className="h-0 px-3 py-2 rounded-md bg-gradient-to-b from-gray-darken/60 to-transparent w-fit h-fit max-w-[90vw] md:max-w-[275px] max-h-[200px] md:max-h-[180px] shadow-md shadow-dark-lighten flex flex-col justify-center items-center"
                      >
                        <YouTube
                          videoId={video.key}
                          opts={{
                            height: isMobile ? "150px" : "125px",
                            width: isMobile ? "300px" : "250px",
                          }}
                        />
                        <p className="mt-3 text-lg tw-multiline-ellipsis-2">
                          {video.name}
                        </p>
                      </li>
                    ))}
                  {!videos &&
                    [...new Array(2)].map((_, index) => (
                      <li key={index}>
                        <div className="w-[275px] h-[180px] relative">
                          <Skeleton className="absolute w-full h-full rounded-md" />
                        </div>
                      </li>
                    ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
        <div className="shrink-0 md:max-w-[310px] w-full relative px-6">
          {!isMobile && <SearchBox />}
          <SideFilmsCol
            name="Similar"
            films={similar?.filter((item) => item.id !== detail?.id)}
            limitNumber={4}
            isLoading={!similar}
            className="md:mt-24 mt-5"
          />
        </div>
      </div>
      <Footer />
    </>
  );
};

export default FilmDetail;
